/**
 * API Status Monitor Component
 * 
 * Monitors health status of multiple API servers with real-time updates
 */

import { useState, useEffect } from 'react';
import { Activity, CheckCircle, XCircle, AlertCircle, RefreshCw, Lock, Shield } from 'lucide-react';
import { adminApi } from '../stores/adminStore';

interface ServerStatus {
  name: string;
  url: string;
  healthEndpoint: string;
  isPublic: boolean; // If false, not accessible from browser
  status: 'online' | 'offline' | 'checking' | 'restricted' | 'degraded';
  responseTime?: number;
  lastChecked?: Date;
  error?: string;
}

const SERVERS: Omit<ServerStatus, 'status' | 'responseTime' | 'lastChecked' | 'error'>[] = [
  {
    name: 'Main API',
    url: 'https://api.metapic.app',
    healthEndpoint: '/health',
    isPublic: true
  },
  {
    name: 'Metadata API',
    url: 'https://metadata.metapic.app',
    healthEndpoint: '/health',
    isPublic: false // Internal only - backend accessible
  },
  {
    name: 'Research API',
    url: 'https://research.metapic.app',
    healthEndpoint: '/health',
    isPublic: false // Internal only - backend accessible
  }
];

export default function ApiStatusMonitor() {
  const [servers, setServers] = useState<ServerStatus[]>(
    SERVERS.map(s => ({ ...s, status: 'checking' as const }))
  );
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const checkAllServers = async () => {
    setIsRefreshing(true);
    
    try {
      // Call backend API which can check internal services
      const response = await adminApi.get('/admin/system/health');
      const data = response.data.data;
      
      // Transform backend response to frontend format
      const statuses: ServerStatus[] = data.services.map((service: any) => ({
        name: service.name,
        url: service.url,
        healthEndpoint: '/health',
        isPublic: !service.isInternal,
        status: service.status,
        responseTime: service.responseTime,
        lastChecked: new Date(service.lastChecked),
        error: service.error
      }));
      
      setServers(statuses);
    } catch (error) {
      console.error('Failed to check system health:', error);
      
      // Fallback to showing error state
      const errorStatuses: ServerStatus[] = SERVERS.map(server => ({
        ...server,
        status: 'offline' as const,
        lastChecked: new Date(),
        error: 'Failed to fetch health status'
      }));
      
      setServers(errorStatuses);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Initial check and auto-refresh
  useEffect(() => {
    checkAllServers();

    if (autoRefresh) {
      const interval = setInterval(checkAllServers, 30000); // Check every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusIcon = (status: ServerStatus['status']) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'degraded':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'restricted':
        return <Shield className="w-5 h-5 text-blue-500" />;
      case 'offline':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'checking':
        return <RefreshCw className="w-5 h-5 text-gray-400 animate-spin" />;
    }
  };

  const getStatusColor = (status: ServerStatus['status']) => {
    switch (status) {
      case 'online':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'degraded':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'restricted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'offline':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'checking':
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getResponseTimeColor = (responseTime?: number) => {
    if (!responseTime) return 'text-gray-500';
    if (responseTime < 200) return 'text-green-600';
    if (responseTime < 500) return 'text-yellow-600';
    if (responseTime < 1000) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatLastChecked = (date?: Date) => {
    if (!date) return 'Never';
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Activity className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">API Status Monitor</h3>
            <p className="text-sm text-gray-600">Real-time server health monitoring</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            Auto-refresh
          </label>
          
          <button
            onClick={checkAllServers}
            disabled={isRefreshing}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {servers.map((server, index) => (
          <div
            key={index}
            className={`border-2 rounded-lg p-4 transition-all ${getStatusColor(server.status)}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  {getStatusIcon(server.status)}
                  {server.name}
                </h4>
                <p className="text-xs text-gray-600 mt-1">{server.url}</p>
              </div>
            </div>

            <div className="space-y-2">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Status:</span>
                <span className={`text-xs font-bold uppercase ${
                  server.status === 'online' ? 'text-green-700' :
                  server.status === 'degraded' ? 'text-yellow-700' :
                  server.status === 'restricted' ? 'text-blue-700' :
                  server.status === 'offline' ? 'text-red-700' :
                  'text-gray-600'
                }`}>
                  {server.status}
                  {server.status === 'restricted' && !server.isPublic && ' (Internal)'}
                </span>
              </div>

              {/* Response Time */}
              {server.responseTime !== undefined && (
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700">Response:</span>
                  <span className={`text-xs font-semibold ${getResponseTimeColor(server.responseTime)}`}>
                    {server.responseTime}ms
                  </span>
                </div>
              )}

              {/* Last Checked */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-700">Last check:</span>
                <span className="text-xs text-gray-600">
                  {formatLastChecked(server.lastChecked)}
                </span>
              </div>

              {/* Error Message */}
              {server.error && (
                <div className="mt-2 pt-2 border-t border-current/20">
                  <div className="flex items-start gap-1">
                    <AlertCircle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-red-700 break-all">{server.error}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Overall Status Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            Overall Status: {servers.filter(s => s.status === 'online').length} of {servers.length} servers online
          </span>
          {autoRefresh && (
            <span className="text-gray-500 text-xs">
              Auto-refreshing every 30 seconds
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
